import { Component } from '@theme/component'
import { prefersReducedMotion } from '@theme/utilities'

/** Matches `--announcement-transition-enter` in header-announcements.liquid */
export const ANNOUNCEMENT_ENTER_MS = 520

/** @type {number} */
const TRANSITION_LOCK_MS = ANNOUNCEMENT_ENTER_MS + 80

/** @type {number} */
const AUTOPLAY_MIN_SECONDS = 2

/** @type {number} */
const AUTOPLAY_MAX_SECONDS = 8

const SLIDE_ENTERING_CLASS = 'announcement-bar__slide--entering'
const SLIDE_EXITING_CLASS = 'announcement-bar__slide--exiting'

/** Matches `--announcement-split-gap` (1.5rem at a 16px root). */
export const ANNOUNCEMENT_SPLIT_GAP_PX = 24

/** Extra width required to leave stacked, so the boundary does not flicker. */
export const ANNOUNCEMENT_SPLIT_HYSTERESIS_PX = 16

/**
 * @param {string | number | null | undefined} value
 * @returns {number|undefined}
 */
export function parseAutoplayIntervalMs(value) {
  const seconds = parseFloat(`${value ?? ''}`)

  if (Number.isNaN(seconds)) return undefined

  const clamped = Math.min(
    AUTOPLAY_MAX_SECONDS,
    Math.max(AUTOPLAY_MIN_SECONDS, seconds)
  )

  return clamped * 1000
}

/**
 * Shortest-path direction for carousel slide changes (wrap-aware).
 *
 * @param {number} fromIndex
 * @param {number} toIndex
 * @param {number} slideCount
 * @returns {'next' | 'previous'}
 */
export function getSlideTransitionDirection(fromIndex, toIndex, slideCount) {
  if (slideCount <= 1) return 'next'

  let delta = toIndex - fromIndex
  const half = slideCount / 2

  if (delta > half) delta -= slideCount
  if (delta < -half) delta += slideCount

  return delta >= 0 ? 'next' : 'previous'
}

/**
 * Resolve a CSS length token (`px` / `rem`) to pixels.
 *
 * @param {string | number | null | undefined} value
 * @param {number} [rootFontSizePx]
 * @returns {number}
 */
export function parseAnnouncementCssLengthPx(value, rootFontSizePx = 16) {
  const raw = String(value ?? '').trim()
  const amount = Number.parseFloat(raw)

  if (!Number.isFinite(amount)) return 0

  const root =
    Number.isFinite(rootFontSizePx) && rootFontSizePx > 0 ? rootFontSizePx : 16

  if (raw.endsWith('rem')) return amount * root

  return amount
}

/**
 * Whether Trustpilot + promo + socials (whichever exist) fit in one row.
 *
 * @param {{
 *   innerWidth: number
 *   columnMins: number[]
 *   gap?: number
 *   hysteresis?: number
 * }} params
 * @returns {boolean}
 */
export function canFitAnnouncementSplit({
  innerWidth,
  columnMins,
  gap = ANNOUNCEMENT_SPLIT_GAP_PX,
  hysteresis = 0
}) {
  const mins = []

  for (const value of columnMins) {
    const width = Number(value) || 0
    if (width > 0) mins.push(width)
  }

  if (mins.length <= 1) return false

  const gapPx = Number(gap) || 0
  const extra = Number(hysteresis) || 0
  const needed =
    mins.reduce((sum, width) => sum + width, 0) + gapPx * (mins.length - 1)

  return (Number(innerWidth) || 0) >= needed + extra
}

/**
 * @param {{
 *   innerWidth: number
 *   columnMins: number[]
 *   gap?: number
 *   hysteresis?: number
 * }} params
 * @returns {'stacked' | 'split'}
 */
export function resolveAnnouncementLayoutMode(params) {
  return canFitAnnouncementSplit(params) ? 'split' : 'stacked'
}

/**
 * Min-content width of a column, including nodes hidden for the stacked layout.
 *
 * @param {Element | null | undefined} el
 * @returns {number}
 */
export function measureAnnouncementColumnMinWidth(el) {
  if (!(el instanceof HTMLElement)) return 0

  const style = el.style
  const previous = {
    position: style.position,
    visibility: style.visibility,
    display: style.display,
    width: style.width,
    maxWidth: style.maxWidth,
    height: style.height,
    paddingInline: style.paddingInline,
    paddingLeft: style.paddingLeft,
    paddingRight: style.paddingRight,
    whiteSpace: style.whiteSpace,
    pointerEvents: style.pointerEvents
  }
  const wasHidden = el.hidden
  const hadInert = el.hasAttribute('inert')
  const hadMeasuring = el.hasAttribute('data-announcement-measuring')

  el.hidden = false
  el.removeAttribute('inert')
  el.setAttribute('data-announcement-measuring', 'true')
  style.position = 'absolute'
  style.visibility = 'hidden'
  style.display = 'flex'
  style.width = 'max-content'
  style.maxWidth = 'none'
  style.height = 'auto'
  style.paddingInline = '0px'
  style.paddingLeft = '0px'
  style.paddingRight = '0px'
  style.whiteSpace = 'nowrap'
  style.pointerEvents = 'none'

  const width = Math.ceil(
    Math.max(el.scrollWidth, el.getBoundingClientRect().width)
  )

  style.position = previous.position
  style.visibility = previous.visibility
  style.display = previous.display
  style.width = previous.width
  style.maxWidth = previous.maxWidth
  style.height = previous.height
  style.paddingInline = previous.paddingInline
  style.paddingLeft = previous.paddingLeft
  style.paddingRight = previous.paddingRight
  style.whiteSpace = previous.whiteSpace
  style.pointerEvents = previous.pointerEvents
  if (!hadMeasuring) el.removeAttribute('data-announcement-measuring')
  el.hidden = wasHidden
  if (hadInert) el.setAttribute('inert', '')

  return Number.isFinite(width) ? width : 0
}

/**
 * Whether an announcement host is in the live (visible) layout.
 *
 * @param {Element | null | undefined} el
 * @returns {boolean}
 */
export function isAnnouncementHostActive(el) {
  if (!(el instanceof Element)) return false
  if (el.closest('[hidden], [inert]')) return false
  return true
}

/**
 * Slides that participate in the carousel for the current layout.
 * Socials never rotate. Split also keeps Trustpilot static.
 *
 * @param {Iterable<Element | null | undefined>} slides
 * @param {'stacked' | 'split'} mode
 * @returns {HTMLElement[]}
 */
export function getAnnouncementCarouselSlides(slides, mode) {
  const list = []

  for (const slide of slides) {
    if (!(slide instanceof HTMLElement)) continue
    if (slide.classList.contains('announcement-bar__slide--socials')) continue
    list.push(slide)
  }

  if (mode !== 'split') return list

  return list.filter((slide) => {
    if (slide.classList.contains('announcement-bar__slide--promo')) return true

    return !slide.classList.contains('announcement-bar__slide--trustpilot')
  })
}

/**
 * @param {HTMLElement} bar
 * @param {'stacked' | 'split'} mode
 */
export function applyAnnouncementSocialsVisibility(bar, mode) {
  const socials = bar.querySelector('.announcement-bar__slide--socials')
  if (!(socials instanceof HTMLElement)) return

  if (mode === 'stacked') {
    socials.hidden = true
    socials.setAttribute('inert', '')
    socials.setAttribute('aria-hidden', 'true')
    return
  }

  socials.hidden = false
  socials.removeAttribute('inert')
}

/**
 * Sync the announcement layout mode onto the bar and notify the slider.
 * No-ops when the mode is already applied so resize does not reset the carousel.
 *
 * @param {ParentNode} root
 * @param {'stacked' | 'split'} mode
 * @returns {boolean}
 */
export function syncAnnouncementLayout(root, mode) {
  if (mode !== 'split' && mode !== 'stacked') return false

  const bars =
    root instanceof Element && root.matches('.announcement-bar')
      ? [root]
      : [...root.querySelectorAll('.announcement-bar')]

  let changed = false

  for (const bar of bars) {
    if (!(bar instanceof HTMLElement)) continue
    applyAnnouncementSocialsVisibility(bar, mode)

    if (bar.dataset.announcementLayout === mode) continue

    bar.dataset.announcementLayout = mode
    changed = true

    for (const slider of bar.querySelectorAll('announcement-bar-component')) {
      slider.dispatchEvent(
        new CustomEvent('announcement:layout-change', { detail: { mode } })
      )
    }
  }

  return changed
}

/**
 * @param {HTMLElement} bar
 * @returns {number[]}
 */
export function measureAnnouncementSplitColumns(bar) {
  const trust = bar.querySelector('.announcement-bar__slide--trustpilot')
  const socials = bar.querySelector('.announcement-bar__slide--socials')
  const promos = bar.querySelectorAll('.announcement-bar__slide--promo')

  let promoMin = 0
  for (const promo of promos) {
    promoMin = Math.max(promoMin, measureAnnouncementColumnMinWidth(promo))
  }

  if (promoMin > 0) {
    const rootFontSize = parseAnnouncementCssLengthPx(
      window.getComputedStyle(document.documentElement).fontSize
    )
    const buttonSize = parseAnnouncementCssLengthPx(
      window.getComputedStyle(bar).getPropertyValue('--button-size'),
      rootFontSize
    )
    promoMin += (buttonSize || 44) * 2

    // The spider is display:none until split, so reserve its slot explicitly.
    if (bar.querySelector('.announcement-bar__spider')) {
      const spiderSlot = parseAnnouncementCssLengthPx(
        window
          .getComputedStyle(bar)
          .getPropertyValue('--announcement-spider-slot'),
        rootFontSize
      )
      promoMin += spiderSlot > 0 ? spiderSlot : 118
    }
  }

  return [
    measureAnnouncementColumnMinWidth(trust),
    promoMin,
    measureAnnouncementColumnMinWidth(socials)
  ]
}

/**
 * Content-box inline width of the announcement inner container (clientWidth minus inline padding).
 *
 * @param {Element | null | undefined} el
 * @param {number} [rootFontSizePx]
 * @returns {number}
 */
export function measureAnnouncementInnerContentWidth(el, rootFontSizePx = 16) {
  if (!(el instanceof HTMLElement)) return 0

  const clientWidth = Number(el.clientWidth) || 0
  if (clientWidth <= 0) return 0

  const styles = window.getComputedStyle(el)
  const paddingLeft = parseAnnouncementCssLengthPx(
    styles.paddingLeft,
    rootFontSizePx
  )
  const paddingRight = parseAnnouncementCssLengthPx(
    styles.paddingRight,
    rootFontSizePx
  )

  return Math.max(0, clientWidth - paddingLeft - paddingRight)
}

/**
 * Measure each announcement bar and apply stacked or split.
 *
 * @param {ParentNode} [root]
 */
export function applyAnnouncementLayoutFromMeasurements(root = document) {
  const bars =
    root instanceof Element && root.matches('.announcement-bar')
      ? [root]
      : [...root.querySelectorAll('.announcement-bar')]

  for (const bar of bars) {
    if (!(bar instanceof HTMLElement)) continue

    const inner = bar.querySelector('.announcement-bar__inner')
    const measureRoot = inner instanceof HTMLElement ? inner : bar
    const currentMode =
      bar.dataset.announcementLayout === 'split' ? 'split' : 'stacked'
    const rootFontSize = parseAnnouncementCssLengthPx(
      window.getComputedStyle(document.documentElement).fontSize
    )
    const gap = parseAnnouncementCssLengthPx(
      window.getComputedStyle(bar).getPropertyValue('--announcement-split-gap'),
      rootFontSize
    )
    const mode = resolveAnnouncementLayoutMode({
      innerWidth: measureAnnouncementInnerContentWidth(
        measureRoot,
        rootFontSize
      ),
      columnMins: measureAnnouncementSplitColumns(bar),
      gap: gap || ANNOUNCEMENT_SPLIT_GAP_PX,
      hysteresis:
        currentMode === 'stacked' ? ANNOUNCEMENT_SPLIT_HYSTERESIS_PX : 0
    })

    syncAnnouncementLayout(bar, mode)
  }
}

let announcementLayoutBound = false
/** @type {number} */
let announcementLayoutFrame = 0
/** @type {WeakSet<Element>} */
const observedAnnouncementInners = new WeakSet()
/** @type {WeakMap<Element, number>} */
const observedAnnouncementInnerWidths = new WeakMap()

/**
 * @param {ParentNode} [root]
 */
export function scheduleAnnouncementLayoutSync(root = document) {
  if (announcementLayoutFrame) return

  announcementLayoutFrame = window.requestAnimationFrame(() => {
    announcementLayoutFrame = 0
    applyAnnouncementLayoutFromMeasurements(root)
  })
}

/**
 * Bind fit-based stacked/split sync once per document.
 *
 * @param {ParentNode} [root]
 */
export function bindAnnouncementLayoutSync(root = document) {
  if (announcementLayoutBound) return

  announcementLayoutBound = true

  /** @type {ResizeObserver | undefined} */
  let resizeObserver

  const observeInner = (inner) => {
    if (!(inner instanceof HTMLElement)) return
    if (observedAnnouncementInners.has(inner)) return
    observedAnnouncementInners.add(inner)
    observedAnnouncementInnerWidths.set(inner, Math.round(inner.clientWidth))
    resizeObserver?.observe(inner)
  }

  const scan = (scope) => {
    if (!(scope instanceof Element) && scope !== document) return
    const inners =
      scope instanceof Element && scope.matches('.announcement-bar__inner')
        ? [scope]
        : [...scope.querySelectorAll('.announcement-bar__inner')]

    for (const inner of inners) observeInner(inner)
    applyAnnouncementLayoutFromMeasurements(
      scope instanceof Element && scope.matches('.announcement-bar')
        ? scope
        : root
    )
  }

  if (typeof ResizeObserver === 'function') {
    resizeObserver = new ResizeObserver((entries) => {
      if (!Array.isArray(entries) || entries.length === 0) {
        scheduleAnnouncementLayoutSync(root)
        return
      }

      let widthChanged = false

      for (const entry of entries) {
        const target = entry?.target
        if (!(target instanceof HTMLElement)) continue

        const nextWidth = Math.round(
          target.clientWidth || entry.contentRect?.width || 0
        )
        const previousWidth = observedAnnouncementInnerWidths.get(target)

        if (previousWidth === nextWidth) continue

        observedAnnouncementInnerWidths.set(target, nextWidth)
        widthChanged = true
      }

      if (widthChanged) {
        scheduleAnnouncementLayoutSync(root)
      }
    })
  } else if (typeof window.addEventListener === 'function') {
    window.addEventListener(
      'resize',
      () => scheduleAnnouncementLayoutSync(root),
      { passive: true }
    )
  }

  scan(root)

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target
    if (section instanceof Element) scan(section)
  })
}

/**
 * Whether the announcement bar is visually collapsed (scroll hide or mega menu).
 *
 * @returns {boolean}
 */
export function isHeaderAnnouncementHidden() {
  const headerGroup = document.getElementById('header-group')
  if (!headerGroup) return false

  return (
    headerGroup.dataset.announcementState === 'hidden' ||
    headerGroup.dataset.megaMenuOpen === 'true'
  )
}

/**
 * @param {number} index
 * @param {number} slideCount
 * @returns {number}
 */
function normalizeSlideIndex(index, slideCount) {
  if (slideCount === 0) return 0

  let relativeIndex = index % slideCount
  if (relativeIndex < 0) relativeIndex += slideCount

  return relativeIndex
}

/**
 * @param {AnimationEvent} event
 * @returns {boolean}
 */
function isAnnouncementEnterAnimation(event) {
  return (
    typeof event.animationName === 'string' &&
    event.animationName.startsWith('announcement-bar-enter')
  )
}

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0

  /**
   * Autoplay dwell timeout (scheduled after each settled slide).
   * @type {number|undefined}
   */
  #autoplayTimeout = undefined

  /**
   * JS-011: pause autoplay while the host is scrolled off-screen.
   * @type {IntersectionObserver|undefined}
   */
  #visibilityObserver = undefined

  /** JS-011: suspended while host is offscreen. @type {boolean} */
  #offscreen = false

  /** @type {boolean} */
  #headerCollapsed = false

  /** @type {MutationObserver|undefined} */
  #headerStateObserver = undefined

  /** @type {boolean} */
  #transitioning = false

  /** @type {number|undefined} */
  #transitionTimeout = undefined

  /** @type {((event: AnimationEvent) => void)|undefined} */
  #transitionEndHandler = undefined

  /** @type {HTMLElement|undefined} */
  #enteringSlide = undefined

  connectedCallback() {
    super.connectedCallback()

    this.addEventListener('mouseenter', this.#handleMouseEnter)
    this.addEventListener('mouseleave', this.#handleMouseLeave)
    this.addEventListener(
      'announcement:layout-change',
      this.#handleLayoutChange
    )
    document.addEventListener('visibilitychange', this.#handleVisibilityChange)

    if (typeof IntersectionObserver !== 'undefined') {
      this.#visibilityObserver = new IntersectionObserver((entries) => {
        const entry = entries[0]
        if (!entry) return
        this.#offscreen = !entry.isIntersecting
        if (this.#offscreen) {
          this.#suspendAutoplay()
        } else {
          this.#resumeAutoplay()
        }
      })
      this.#visibilityObserver.observe(this)
    }

    const headerGroup = document.getElementById('header-group')
    if (headerGroup) {
      this.#headerCollapsed = isHeaderAnnouncementHidden()
      this.#headerStateObserver = new MutationObserver(() => {
        const collapsed = isHeaderAnnouncementHidden()
        if (collapsed === this.#headerCollapsed) return

        this.#headerCollapsed = collapsed
        if (collapsed) {
          this.#suspendAutoplay()
        } else {
          this.#resumeAutoplay()
        }
      })
      this.#headerStateObserver.observe(headerGroup, {
        attributes: true,
        attributeFilter: ['data-announcement-state', 'data-mega-menu-open']
      })
    }

    this.#applySlideState(
      normalizeSlideIndex(this.#current, this.#slideCount())
    )
    this.#scheduleAutoplay()
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    this.#suspendAutoplay()
    this.#clearTransition()
    this.#visibilityObserver?.disconnect()
    this.#visibilityObserver = undefined
    this.#headerStateObserver?.disconnect()
    this.#headerStateObserver = undefined
    this.removeEventListener('mouseenter', this.#handleMouseEnter)
    this.removeEventListener('mouseleave', this.#handleMouseLeave)
    this.removeEventListener(
      'announcement:layout-change',
      this.#handleLayoutChange
    )
    document.removeEventListener(
      'visibilitychange',
      this.#handleVisibilityChange
    )
  }

  next() {
    if (this.#transitioning) return
    this.current += 1
  }

  previous() {
    if (this.#transitioning) return
    this.current -= 1
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in milliseconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return

    this.paused = false
    this.#scheduleAutoplay(interval)
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true
    this.#suspendAutoplay()
  }

  get paused() {
    return this.hasAttribute('paused')
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused)
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    this.#suspendAutoplay()
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    this.#resumeAutoplay()
  }

  get autoplay() {
    return Boolean(this.autoplayInterval)
  }

  get autoplayInterval() {
    return parseAutoplayIntervalMs(this.getAttribute('autoplay'))
  }

  get current() {
    return this.#current
  }

  set current(current) {
    const slides = this.#carouselSlides()
    const slideCount = slides.length
    if (slideCount === 0) return

    const previousRelative = normalizeSlideIndex(this.#current, slideCount)
    this.#current = current
    const relativeIndex = normalizeSlideIndex(current, slideCount)

    this.#suspendAutoplay()

    if (previousRelative === relativeIndex) {
      this.#applySlideState(relativeIndex)
      this.#scheduleAutoplay()
      return
    }

    if (prefersReducedMotion()) {
      this.#applySlideState(relativeIndex)
      this.#scheduleAutoplay()
      return
    }

    const direction = getSlideTransitionDirection(
      previousRelative,
      relativeIndex,
      slideCount
    )

    this.#runTransition(slides, previousRelative, relativeIndex, direction)
  }

  /**
   * @returns {HTMLElement[]}
   */
  #allSlides() {
    const slides = this.refs.slides
    if (!slides) return []
    return Array.isArray(slides) ? slides : [slides]
  }

  /**
   * @returns {'stacked' | 'split'}
   */
  #layoutMode() {
    const bar = this.closest('.announcement-bar')
    const fromDom = bar?.dataset.announcementLayout
    if (fromDom === 'split' || fromDom === 'stacked') return fromDom

    return 'stacked'
  }

  /**
   * @returns {HTMLElement[]}
   */
  #carouselSlides() {
    return getAnnouncementCarouselSlides(this.#allSlides(), this.#layoutMode())
  }

  #pinStaticColumnSlides() {
    if (this.#layoutMode() !== 'split') return

    for (const slide of this.#allSlides()) {
      if (
        slide.classList.contains('announcement-bar__slide--trustpilot') ||
        slide.classList.contains('announcement-bar__slide--socials')
      ) {
        slide.setAttribute('aria-hidden', 'false')
        slide.classList.remove(SLIDE_ENTERING_CLASS, SLIDE_EXITING_CLASS)
      }
    }
  }

  #handleLayoutChange = () => {
    this.#current = 0
    this.#clearTransition()
    this.#applySlideState(0)
    this.#scheduleAutoplay()
  }

  /**
   * @returns {number}
   */
  #slideCount() {
    return this.#carouselSlides().length
  }

  /**
   * @param {number} [interval]
   */
  #scheduleAutoplay(interval = this.autoplayInterval) {
    this.#suspendAutoplay()

    if (this.#slideCount() <= 1) return
    if (!interval || this.paused || !this.#canAutoplayAdvance()) return

    this.#autoplayTimeout = window.setTimeout(() => {
      this.#autoplayTimeout = undefined
      if (!this.#canAutoplayAdvance()) return
      this.next()
    }, interval)
  }

  #suspendAutoplay() {
    if (this.#autoplayTimeout !== undefined) {
      clearTimeout(this.#autoplayTimeout)
      this.#autoplayTimeout = undefined
    }
  }

  #resumeAutoplay() {
    if (!this.autoplay || this.paused) return
    if (this.#offscreen || isHeaderAnnouncementHidden()) return
    if (!isAnnouncementHostActive(this)) return

    this.#scheduleAutoplay()
  }

  #canAutoplayAdvance() {
    if (
      document.documentElement.hasAttribute('data-theme-debug-freeze-motion')
    ) {
      return false
    }
    if (!this.autoplay || this.paused || this.#transitioning) return false
    if (this.#slideCount() <= 1) return false
    if (this.matches(':hover') || document.hidden || this.#offscreen) {
      return false
    }
    if (isHeaderAnnouncementHidden()) return false
    if (!isAnnouncementHostActive(this)) return false

    if (typeof this.checkVisibility === 'function') {
      if (!this.checkVisibility({ checkVisibilityCSS: true })) return false
    } else if (this.offsetParent === null && this.style.display === 'none') {
      return false
    }

    return true
  }

  /**
   * @param {number} relativeIndex
   */
  #applySlideState(relativeIndex) {
    const slides = this.#carouselSlides()

    slides.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`)
      slide.classList.remove(SLIDE_ENTERING_CLASS, SLIDE_EXITING_CLASS)
    })

    const bar = this.closest('.announcement-bar')
    if (bar instanceof HTMLElement) {
      applyAnnouncementSocialsVisibility(bar, this.#layoutMode())
    }

    this.#pinStaticColumnSlides()
  }

  /**
   * @param {HTMLElement[]} slides
   * @param {number} fromIndex
   * @param {number} toIndex
   * @param {'next' | 'previous'} direction
   */
  #runTransition(slides, fromIndex, toIndex, direction) {
    this.#clearTransition()

    const outgoing = slides[fromIndex]
    const incoming = slides[toIndex]

    if (!outgoing || !incoming) {
      this.#applySlideState(toIndex)
      this.#scheduleAutoplay()
      return
    }

    this.#transitioning = true
    this.dataset.transitionDirection = direction

    slides.forEach((slide, index) => {
      slide.classList.remove(SLIDE_ENTERING_CLASS, SLIDE_EXITING_CLASS)

      if (index === fromIndex) {
        slide.setAttribute('aria-hidden', 'true')
        slide.classList.add(SLIDE_EXITING_CLASS)
        return
      }

      if (index === toIndex) {
        slide.setAttribute('aria-hidden', 'false')
        slide.classList.add(SLIDE_ENTERING_CLASS)
        return
      }

      slide.setAttribute('aria-hidden', 'true')
    })

    this.#enteringSlide = incoming
    this.#transitionEndHandler = (event) => {
      if (event.target !== incoming) return
      if (!isAnnouncementEnterAnimation(event)) return
      this.#finishTransition(slides, toIndex)
    }

    incoming.addEventListener('animationend', this.#transitionEndHandler)

    this.#transitionTimeout = window.setTimeout(() => {
      this.#finishTransition(slides, toIndex)
    }, TRANSITION_LOCK_MS)
  }

  /**
   * @param {HTMLElement[]} _slides
   * @param {number} relativeIndex
   */
  #finishTransition(_slides, relativeIndex) {
    if (!this.#transitioning) return

    this.#clearTransition()
    this.#applySlideState(relativeIndex)
    this.#scheduleAutoplay()
  }

  #clearTransition() {
    this.#transitioning = false
    delete this.dataset.transitionDirection

    if (this.#transitionTimeout !== undefined) {
      clearTimeout(this.#transitionTimeout)
      this.#transitionTimeout = undefined
    }

    if (this.#enteringSlide && this.#transitionEndHandler) {
      this.#enteringSlide.removeEventListener(
        'animationend',
        this.#transitionEndHandler
      )
    }

    this.#enteringSlide = undefined
    this.#transitionEndHandler = undefined
    this.#carouselSlides().forEach((slide) => {
      slide.classList.remove(SLIDE_ENTERING_CLASS, SLIDE_EXITING_CLASS)
    })
  }

  #handleMouseEnter = () => {
    this.#suspendAutoplay()
  }

  #handleMouseLeave = () => {
    this.#resumeAutoplay()
  }

  #handleVisibilityChange = () => {
    if (document.hidden) {
      this.#suspendAutoplay()
      return
    }

    this.#resumeAutoplay()
  }
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar)
}

bindAnnouncementLayoutSync()
